import { FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { Form } from "antd";
import { FormInstance } from "antd/lib/form";
import { isArrayAndNotEmpty } from "@src/util/common/array";
import React from "react";
import { useLoadAbExploration } from "@src/hooks/ab-testing/use-ab-exploration-get-result";
import { SupportedModelAlias } from "@src/constant/ab-testing/ab-testing-exploration.enum";

type UseGetExtraValueFieldProps = {
  form: FormInstance;
  dimension: string[];
  limit?: number;
  skip?: boolean;
};

export const useGetExtraValueField = ({ form, dimension, limit = 10000, skip = false }: UseGetExtraValueFieldProps) => {
  const previousQueryKey = React.useRef<string>();

  const productCodeWatch = Form.useWatch(FilterFormFieldsEnum.PRODUCT_CODE, form);

  const abaIdWatch = Form.useWatch(FilterFormFieldsEnum.ABA_ID, form);
  const abaIdLoading = Form.useWatch(FilterFormFieldsEnum.IS_ABA_LOADING, form);
  const amaAppLoading = Form.useWatch(FilterFormFieldsEnum.IS_AMA_APP_LOADING, form);
  const expLoading = Form.useWatch(FilterFormFieldsEnum.IS_EXP_LOADING, form);
  const isOpenModal = Form.useWatch(FilterFormFieldsEnum.IS_OPEN_MODAL, form);

  const isRequiredFieldLoading = abaIdLoading || amaAppLoading || expLoading;

  const variantWatch = Form.useWatch(FilterFormFieldsEnum.VARIANTS, form);

  const amaAppIdWatch = Form.useWatch(FilterFormFieldsEnum.AMA_APP_IDS, form);
  const _skip = skip || isRequiredFieldLoading || !isOpenModal;
  const newQueryKey = JSON.stringify({
    productCode: productCodeWatch,
    abaId: abaIdWatch,
    amaAppIds: amaAppIdWatch,
    variants: variantWatch,
    dimension,
    limit,
    skip: _skip,
  });
  const { getOptions, isLoading, removeRedundantData, refetch } = useLoadDimValue({
    form,
    productCode: productCodeWatch,
    abaId: abaIdWatch,
    amaAppIds: amaAppIdWatch,
    variants: variantWatch,
    dimension,
    limit,
    skip: true,
  });
  React.useEffect(() => {
    if (previousQueryKey.current === newQueryKey || _skip) return;
    previousQueryKey.current = newQueryKey;
    const timeoutId = setTimeout(() => {
      refetch();
    }, 200);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [newQueryKey, _skip]);

  return {
    getOptions,
    isLoading,
    removeRedundantData,
  };
};

export const useLoadDimValue = ({
  form,
  productCode,
  variants,
  abaId,
  amaAppIds,
  dimension,
  limit,
  skip,
}: {
  form?: FormInstance;
  variants: string[] | undefined;
  abaId: string | undefined;
  amaAppIds: string[] | undefined;
  productCode: string | undefined;
  dimension: string[];
  limit?: number;
  skip?: boolean;
}) => {
  const filters: any = React.useMemo(() => {
    return [
      abaId
        ? {
            member: "aba_id",
            operator: "equals",
            values: [abaId.toString()],
          }
        : null,
      variants && variants?.length > 0
        ? {
            member: "exp_group",
            operator: "equals",
            values: variants,
          }
        : null,
      amaAppIds && amaAppIds?.length > 0
        ? {
            member: "ama_app_id",
            operator: "equals",
            values: amaAppIds,
          }
        : null,
    ].filter((f) => !!f);
  }, [abaId, amaAppIds, variants]);

  const isSkip = !productCode || !abaId || !isArrayAndNotEmpty(amaAppIds ?? []);

  const { result, isLoading, refetch } = useLoadAbExploration({
    productCode: productCode,
    modelAlias: SupportedModelAlias.USER_INFO,
    query: {
      dimensions: dimension,
      measures: ["user_count"],
      filters,
      limit,
      order: [["user_count", "desc"]],
    },
    isSkip: skip || isSkip,
  });

  const getOptions = React.useCallback(
    (
      _dimension: string,
      cb?: {
        labelMapping?: (item: any) => string;
        valueMapping?: (item: any) => string;
      },
    ) => {
      const { labelMapping, valueMapping } = cb || {};

      return result?.map((item: any) => {
        const data = item[_dimension];

        return {
          label: labelMapping ? labelMapping(data) : data,
          value: valueMapping ? valueMapping(data) : data,
          ...item,
        };
      });
    },
    [result],
  );

  const isResultEmpty = !!result;

  const removeRedundantData = React.useCallback(
    ({ field_name, newOptions }: { field_name: string; newOptions: any[] | undefined }) => {
      if (isResultEmpty || !form) return;
      const previousFormValues = form.getFieldValue(field_name);
      if (!previousFormValues || !newOptions) return;

      form.setFieldsValue({
        [field_name]: previousFormValues?.filter((v: string) => newOptions?.some((o: any) => o.value === v)),
      });
    },
    [form, isResultEmpty],
  );

  return { isLoading, getOptions, removeRedundantData, refetch };
};
